import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAuth } from '@/contexts/AuthContext';
import { useLogin } from '@/api/auth/use-auth';
import { useCustomerLogin } from '@/api/auth/use-customer-auth';
import { Eye, EyeOff } from 'lucide-react';
import { useSystemStatus } from '@/api/system-configuration/use-system-configuration';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AddToHomeScreenButton } from '@/components/a2hs/AddToHomeScreenButton';

const LoginPage: React.FC = () => {
    const { t } = useTranslation('auth');
    const { t: tCommon } = useTranslation('common');
    const { isAdmin, isCRM, isTrader } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { data: systemStatus, isLoading: isSystemStatusLoading } = useSystemStatus();

    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<Record<string, string[]>>({});

    // Determine which login mutation to use
    const targetUserType = isTrader() ? 'customer' : 'user';
    const userLoginMutation = useLogin();
    const customerLoginMutation = useCustomerLogin();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: [],
            }));
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (targetUserType === 'customer') {
            customerLoginMutation.mutate(
                { email: formData.email, password: formData.password },
                {
                    onSuccess: () => {
                        enqueueSnackbar(t('loginSuccess'), {
                            variant: 'success',
                            autoHideDuration: 3000,
                        });
                        navigate('/');
                    },
                    onError: (error: any) => {
                        const status = error?.response?.status;
                        const message =
                            status >= 400 && status < 500
                                ? t('incorrectCredentials')
                                : error?.message || t('loginFailed');
                        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });

                        if (error.response?.data?.errors) {
                            setErrors(error.response.data.errors);
                        }
                    },
                }
            );
        } else {
            userLoginMutation.mutate(
                { email: formData.email, password: formData.password },
                {
                    onSuccess: () => {
                        enqueueSnackbar(t('loginSuccess'), {
                            variant: 'success',
                            autoHideDuration: 3000,
                        });
                        navigate('/');
                    },
                    onError: (error: any) => {
                        const status = error?.response?.status;
                        const message =
                            status >= 400 && status < 500
                                ? t('incorrectCredentials')
                                : error?.message || t('loginFailed');
                        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });

                        if (error.response?.data?.errors) {
                            setErrors(error.response.data.errors);
                        }
                    },
                }
            );
        }
    };

    const isLoading = userLoginMutation.isPending || customerLoginMutation.isPending;

    const getPortalInfo = () => {
        if (isAdmin()) {
            return {
                title: t('adminPortalTitle'),
                subtitle: t('adminPortalSubtitle'),
                accountType: t('adminAccount'),
            };
        } else if (isCRM()) {
            return {
                title: t('crmPortalTitle'),
                subtitle: t('crmPortalSubtitle'),
                accountType: t('crmAccount'),
            };
        } else {
            return {
                title: t('traderPortalTitle'),
                subtitle: t('traderPortalSubtitle'),
                accountType: t('traderAccount'),
                registerText: t('noTraderAccount'),
                registerLink:
                    systemStatus?.registration_enabled === true ? '/auth/register' : undefined,
                registerLinkText: t('createTraderAccount'),
            };
        }
    };

    const portalInfo = getPortalInfo();

    if (isSystemStatusLoading) {
        return (
            <>
                <h1 className="mb-2 text-center text-[length:var(--auth-title-size)] font-bold leading-tight tracking-tight">
                    {tCommon('loading')}
                </h1>
                <p className="text-muted-foreground text-center mb-8">{t('pleaseWaitStatus')}</p>
            </>
        );
    }

    if (isTrader() && systemStatus?.maintenance_mode === true) {
        return (
            <>
                <h1 className="mb-2 text-center text-[length:var(--auth-title-size)] font-bold leading-tight tracking-tight">
                    {t('systemMaintenance')}
                </h1>
                <p className="text-muted-foreground text-center mb-8">
                    {systemStatus.maintenance_message || t('maintenanceDefaultMessage')}
                </p>

                <div className="text-center space-y-4">
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span>{t('tradingUnavailable')}</span>
                    </div>

                    <Button onClick={() => window.location.reload()} variant="outline" size="sm">
                        {tCommon('checkAgain')}
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
            <h1 className="mb-2 text-center text-[length:var(--auth-title-size)] font-bold leading-tight tracking-tight">
                {portalInfo.title}
            </h1>
            <p className="text-muted-foreground text-center mb-8">{portalInfo.subtitle}</p>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="email">{t('emailAddress')}</Label>
                    <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={errors.email ? 'border-destructive' : ''}
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email[0]}</p>}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="password">{t('password')}</Label>
                    <div className="relative">
                        <Input
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={handleChange}
                            required
                            className={errors.password ? 'border-destructive pe-10' : 'pe-10'}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                            {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                            ) : (
                                <Eye className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    {errors.password && (
                        <p className="text-sm text-destructive">{errors.password[0]}</p>
                    )}
                </div>

                <div className="text-end">
                    <Link
                        to="/auth/forgot-password"
                        className="text-sm text-primary hover:underline"
                    >
                        {t('forgotPassword')}
                    </Link>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? t('signingIn') : t('signIn')}
                </Button>

                <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                        {portalInfo.registerText}
                        {portalInfo.registerLink && (
                            <>
                                {' '}
                                <Link
                                    to={portalInfo.registerLink}
                                    className="text-primary hover:underline"
                                >
                                    {portalInfo.registerLinkText}
                                </Link>
                            </>
                        )}
                    </p>
                </div>
            </form>

            {/* Add to Home Screen button - Outside form card, Trader portal only, mobile only */}
            {isTrader() && (
                <div className="lg:hidden mt-4">
                    <AddToHomeScreenButton variant="image" />
                </div>
            )}
        </>
    );
};

export default LoginPage;
